import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';
import { settingsKeys } from '../components/settings/shared/settings-queries';
import { API_BASE } from './api-base';
import { useGenericBi } from './use-generic-bi';
import { usePageLoadBi } from './use-page-load-bi';
import { useLoadBi } from './use-load-bi';
import { useBasePath } from './use-base-path';
import { useActiveConversation } from '../components/chat/ActiveConversationContext';
import { waitForRecipientVisible } from './wait-for-recipient';
import { useTranslation } from './i18n';
import { logSignupOnce } from './appsflyer';
import { ensureMe } from './me';
import { claimStoredReferral } from './claim-stored-referral';
import { redeemStoredPromo } from './redeem-promo';
import { wasCodeSent } from './phone-verification-result';
import { sendVerificationCode } from './send-verification-code';
import { useSendCodeBi } from './use-send-code-bi';
import {
  BARE_REFUSAL_WAIT_SECONDS,
  cooldownDeadline,
  secondsUntilResend,
} from './resend-cooldown';

export const CODE_LENGTH = 6;

/** Exported so the page can fire its page-view with the SAME src:172 identity its clicks report. */
export const PHONE_CODE_PAGE = {
  page_name: 'enter_phone_code_page',
  page_version: 'v3_onboarding_enter_phone_code',
} as const;
const PAGE = PHONE_CODE_PAGE;

type CodeMethod = 'sms' | 'voice' | 'whatsapp';
type ResendStatus = 'idle' | 'sending' | 'sent';

// What the current error is ABOUT. Only `code` is a statement about the digits the
// user typed, so only `code` may tint the field or mark it aria-invalid — a failed
// resend or a lost verification session says nothing about what they entered.
//
// `tooSoon` is the server's 429 with a wait attached. It is the one kind whose MESSAGE is not fixed at
// the moment it is set: it counts down, so it is rendered from the live `resendIn` at the bottom of the
// hook rather than baked into a string here.
type ErrorKind = 'code' | 'resend' | 'session' | 'tooSoon';

/**
 * What was sent, and where the step goes when it finishes.
 *
 * `sent` is supplied by a caller that already holds it in memory. That caller is the reason this is a
 * parameter at all: the number and the verificationId reach the standalone page THROUGH THE URL, and a
 * URL is broadcast — every analytics tag on the page reports the page location, so an
 * `/onboarding/code?phone=…&verificationId=…` load hands both to Facebook, Google Ads, DoubleClick and
 * the GTM container as page-view data. Passing them in memory keeps them off the URL, out of the
 * beacons, out of the browser history and out of any Referer.
 */
export interface CodeVerificationInput {
  sent?: { phone: string; verificationId: string; method: CodeMethod };
  onVerified?: () => void;
  onUseAnotherNumber?: () => void;
}

// Instagram's in-app browser hooks `focus()` on form fields (`logLoginFormFocused`) and its bridge
// `postMessage` throws InvalidAccessError (Sentry ORION-WEB-8M). Focus is a convenience here, never
// load-bearing, so a host that throws on it must not turn into an unhandled error.
function focusInput(input: HTMLInputElement | null | undefined): void {
  try {
    input?.focus();
  } catch {
    /* the host's focus hook threw; the box is still there to tap */
  }
}

// Everything the code step does apart from render it — shared by both brand trees
// (see components/{symphony,orion}/onboarding/CodeScreen.tsx). The SMS/voice and
// WhatsApp paths verify in different places (WhatsApp has no verificationId; the
// recipient service checks the code itself and rejects with HTTP 400), and the
// 409 phone-in-use branch is a distinct outcome rather than an error — so this
// behaviour is kept in one place.
export function useCodeVerification({ sent, onVerified, onUseAnotherNumber }: CodeVerificationInput = {}) {
  const router = useRouter();
  const basePath = useBasePath();
  const queryClient = useQueryClient();
  const { activeConversationId, setActiveConversationId } = useActiveConversation();
  // The thread the verification step was opened over — the URL is authoritative, since that is what the
  // step was navigated to with, and the context is the fallback for a step opened without one.
  const conversationId = typeof router.query.conversation === 'string' ? router.query.conversation : (activeConversationId ?? '');
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi('onboarding-code');
  const logSendCode = useSendCodeBi('onboarding-code');

  usePageLoadBi(PAGE);

  const phone = sent ? sent.phone : typeof router.query.phone === 'string' ? router.query.phone : '';
  const initialVerificationId = sent
    ? sent.verificationId
    : typeof router.query.verificationId === 'string'
      ? router.query.verificationId
      : '';
  const initialMethod: CodeMethod = sent
    ? sent.method
    : router.query.method === 'voice'
      ? 'voice'
      : router.query.method === 'whatsapp'
        ? 'whatsapp'
        : 'sms';

  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState<{ message: string; kind: ErrorKind } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [method, setMethod] = useState<CodeMethod>(initialMethod);
  const [resend, setResend] = useState<ResendStatus>('idle');
  // The wait belongs to a CODE that went out, so it is armed when a code step is entered — when `sent`
  // arrives — and not when this hook mounts. The two are the same moment for `PhoneVerifyModal`, which
  // mounts the hook only once it has a code; they are nowhere near each other for
  // `PostPurchasePhoneGate`, which calls the hook above its own `open` check and keeps ONE instance
  // across both steps. Armed on mount, that gate's first code step would arrive with live buttons as
  // soon as the gate had been open 30s — the bug this whole thing exists to prevent, on the one surface
  // nobody looked at.
  //
  // It is NOT persisted with the rest of the pending send: a reload restarts the wait, which is stricter
  // than the truth and never a way around it.
  const [cooldownUntil, setCooldownUntil] = useState(() => (sent ? cooldownDeadline(Date.now()) : 0));
  // The seconds left are DERIVED from the deadline, never held alongside it. Held, they lag every arming
  // by one commit — the interval that refreshed them only runs after paint — so the step rendered once
  // with the buttons looking live and no hint while `sendCode` was already refusing the press. That is
  // the same hole as arming the deadline in an effect, one layer up, and it reappeared at each of the
  // three arming sites. Derived, there is no site to forget: the tick below only forces the re-render.
  const [, setTick] = useState(0);
  const resendIn = secondsUntilResend(cooldownUntil, Date.now());
  // Consecutive 429s that carried no `Retry-After`. The cooldown means a second one can only be pressed
  // after the first one's wait ran out — so two in a row is "we waited and it still refuses", which is
  // the edge's spent budget rather than the timed wall. See the note on `retryAfterSeconds`.
  //
  // Reset whenever a new code step is entered — see `sentKey` just below, which owns that rule.
  const bareRefusals = useRef(0);
  // Which code step a resend belongs to, bumped by the step reset below. Resetting the streak is only half
  // of starting a new step: "use a different number" is deliberately not gated on a resend in flight —
  // abandoning a slow send is the point — so the old request can land AFTER the reset and write its
  // deadline, its streak, its error and its verificationId into the step the user is now looking at. A
  // bare 429 from it puts the streak back to 1, and the FIRST bare refusal on the new number then reads as
  // the second consecutive one: the permanent dead end, for someone who never served a wait. `sendCode`
  // captures this when it starts and writes nothing once it has moved. Both phones' `phoneFlowGeneration`.
  const stepGeneration = useRef(0);
  // Which code the wait above was armed for. A caller that swaps the code step out and back in (the
  // gate's "use a different number") hands us a different one, and that is a NEW step: it starts its own
  // wait and, critically, drops the streak. One refusal here and one on a different number is two
  // people's worth of being early, not a spent budget — reading it as one shows the permanent dead end
  // to someone who was never made to wait. Both phones reset it in `phoneBackToEnter` for this reason;
  // web cannot rely on the hook unmounting, because on the gate it does not.
  const sentKey = sent ? `${sent.phone}|${sent.verificationId}|${sent.method}` : '';
  const [armedFor, setArmedFor] = useState(sentKey);
  if (armedFor !== sentKey) {
    // Adjusted DURING render, not in an effect, and that is the point: an effect runs after the commit
    // is painted, so the code step would show for one frame with live buttons and a press in that frame
    // would be honoured — `sendCode` reads the deadline, which is still 0 until the effect lands. React
    // re-runs the component immediately on a set during render, before anything reaches the screen.
    setArmedFor(sentKey);
    setCooldownUntil(sentKey ? cooldownDeadline(Date.now()) : 0);
    bareRefusals.current = 0;
    // A resend still on the wire now belongs to the step being left, so its answer is dropped when it
    // lands — and the two things that answer would have cleared are cleared here instead. The spinner,
    // because `sendCode` refuses every press while it is up, so a stale one would hold both buttons on
    // the new step until a reply nobody is waiting for. The error, because it was about the old number:
    // "couldn't resend the code" to a number the user has just replaced is about nothing on screen. Both
    // phones clear theirs in `phoneBackToEnter` for the same reason.
    stepGeneration.current += 1;
    setResend('idle');
    setError(null);
  }
  const [verificationId, setVerificationId] = useState(initialVerificationId);
  const [phoneInUse, setPhoneInUse] = useState(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  // Re-entry guard for the auto-submit effect. Using a ref instead of `submitting`
  // in the dep array stops the effect from re-running when we flip submitting=true
  // mid-flow — that re-run was firing the cleanup and short-circuiting the
  // still-in-flight recipient call.
  const inFlightRef = useRef(false);

  // Keep verificationId/method in sync with the query param if the page is arrived at
  // directly (e.g. iteration tab) and the param changes.
  useEffect(() => {
    if (!router.isReady) return;
    if (initialVerificationId && initialVerificationId !== verificationId) setVerificationId(initialVerificationId);
    if (initialMethod !== method) setMethod(initialMethod);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, initialVerificationId, initialMethod]);

  const value = digits.join('');
  const complete = value.length === CODE_LENGTH && /^\d{6}$/.test(value);

  // Tracks the /api/verify-code (+ /api/recipient) submit region; on success the page
  // navigates away, so loadFinish fires only on a failure/phone-in-use settle.
  useLoadBi('code-verification', { isLoading: submitting, isError: !!error });

  // Verification runs off the six digits being complete — mirroring mobile, which submits as
  // soon as the code is in rather than waiting for a press. Also exposed so the Verify button
  // and Enter submit the same way; in practice auto-submit gets there first, so the button
  // spends most of its life disabled and is the affordance rather than the trigger.
  function submit() {
    if (!complete || inFlightRef.current) return;
    const isWhatsApp = method === 'whatsapp';
    // WhatsApp has no verify-code step — the recipient service verifies the code
    // itself — so only the SMS/voice path needs a verificationId up front.
    if (!isWhatsApp && !verificationId) {
      setError({ message: t('onboarding.code.error.missingVerificationId'), kind: 'session' });
      // Clear, like every other rejection: leaving them on screen meant the next successful
      // resend re-armed this effect (it is keyed on verificationId) and spent the freshly sent
      // code on the stale digits, reporting "wrong code" before the SMS had even arrived.
      setDigits(Array(CODE_LENGTH).fill(''));
      focusInput(inputsRef.current[0]);
      return;
    }
    inFlightRef.current = true;
    setSubmitting(true);
    setError(null);
    logPageClickBi({ ...PAGE, button_name: 'verify' });
    (async () => {
      try {
        if (!isWhatsApp) {
          const verifyRes = await fetch(`${API_BASE}/api/verify-code`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: value, verificationId }),
          });
          if (!verifyRes.ok) throw new Error(`${verifyRes.status}`);
          const verify = (await verifyRes.json()) as { success?: boolean; failureReason?: string | null };
          if (!verify.success) {
            reject(verify.failureReason);
            return;
          }
        }

        // Verified — create / fetch the recipient with this phone. For SMS/voice the
        // verification proof rides along so the server can run its verified-phone reclaim
        // (detach the phone from any other recipient that holds it) instead of dying on the
        // uniqueness index.
        const recipientRes = await fetch(`${API_BASE}/api/recipient`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone,
            phoneVerificationCode: value,
            // WhatsApp carries no verificationId — the recipient service checks the code itself.
            ...(isWhatsApp ? {} : { verificationId }),
            // Names where to celebrate. Absent on the standalone pages, which are not in a conversation
            // yet, so they simply get no card.
            ...(conversationId ? { conversationId } : {}),
          }),
        });
        if (recipientRes.status === 409) {
          setPhoneInUse(true);
          setSubmitting(false);
          return;
        }
        if (isWhatsApp && recipientRes.status === 400) {
          const failure = (await recipientRes.json().catch(() => ({}))) as { failureReason?: string | null };
          reject(failure.failureReason);
          return;
        }
        if (!recipientRes.ok) throw new Error(`recipient ${recipientRes.status}`);
        // The number just landed on the recipient, so every cached copy of it is now wrong. Nothing
        // else refetches it: the shared client does not refetch on focus or mount, and the recipient
        // query stops polling the moment it holds data — so a reader would serve "no phone" for the
        // rest of the session. Both exits below need this, the in-app one because it stays on the page
        // it verified from, and the page one because navigating home is a client push that keeps the
        // cache. What goes stale today is the rail's connect destination; anything else keyed on the
        // number inherits the same fix.
        await queryClient.invalidateQueries({ queryKey: settingsKeys.recipient });
        // Signup. The primary trigger is the fresh-account path (ensureMe → logSignupIfNewAccount);
        // this phone-verification path is the fallback for when the account age is unavailable. Both
        // share one per-account latch, so signup fires at most once. ensureMe also pushes the CUID
        // first (so the event attributes to the person) and swallows its own errors, so a failed/slow
        // /api/me never blocks or drops signup.
        const me = await ensureMe();
        logSignupOnce(me?.userId);
        // The phone is now on the recipient, which is the first moment a referral can be recorded (the
        // service refuses without both an email and a phone). Ahead of the branch below because BOTH
        // exits need it — the in-app one returns, so anything after it would only ever run for a user
        // who came through onboarding. Best-effort by construction: it never throws.
        await claimStoredReferral(me?.userId ?? '');
        // Same moment, same reason: a promo link opened before this account existed answered
        // `retry`, and this is the first point its metasite can receive credits. Best-effort by
        // construction — it never throws.
        await redeemStoredPromo(me?.userId ?? '');
        if (onVerified) {
          // A caller already inside the app: it is not entering, so it neither waits for the home
          // gate to see the recipient nor resets the conversation pointer — the thread it verified
          // from is the one it goes back to.
          onVerified();
          return;
        }
        // Wait for the just-created recipient to be visible to the home gate before the home
        // navigation, or it races the search index and bounces back to onboarding. Clear any
        // prior active-conversation pointer FIRST (the provider persists across this soft nav)
        // so the client's derived onboarding kickoff opens a clean thread.
        await waitForRecipientVisible(API_BASE);
        setActiveConversationId(null);
        router.push(`${basePath}/`);
      } catch (err) {
        console.warn('[verify-code]', err);
        reject(undefined, true);
      } finally {
        inFlightRef.current = false;
      }
    })();

    function reject(failureReason?: string | null, generic = false) {
      setError({
        message:
          !generic && failureReason === 'BAD_CODE'
            ? t('onboarding.code.error.wrongCode')
            : t('onboarding.code.error.generic'),
        kind: 'code',
      });
      setDigits(Array(CODE_LENGTH).fill(''));
      setSubmitting(false);
      focusInput(inputsRef.current[0]);
    }
  }

  useEffect(() => {
    submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete, value, verificationId, phone, router, method]);

  // Resend confirmation clears after 3s, matching mobile.
  useEffect(() => {
    if (resend !== 'sent') return;
    const timer = setTimeout(() => setResend('idle'), 3000);
    return () => clearTimeout(timer);
  }, [resend]);

  // Ticks the countdown down to 0 and then stops itself — nothing keeps a timer alive once the buttons
  // are live again. Faster than once a second so the number never sticks a beat late on a tab that was
  // throttled; the rounding in `secondsUntilResend` is what makes the extra ticks free.
  useEffect(() => {
    if (!cooldownUntil) return;
    const timer = setInterval(() => {
      setTick((t) => t + 1);
      if (secondsUntilResend(cooldownUntil, Date.now()) === 0) clearInterval(timer);
    }, 250);
    return () => clearInterval(timer);
  }, [cooldownUntil]);

  // "You can ask again in 0s" is not a sentence, so the wait's own message goes when the wait does.
  useEffect(() => {
    if (resendIn === 0) setError((prev) => (prev?.kind === 'tooSoon' ? null : prev));
  }, [resendIn]);

  // Autofill (SMS one-time-code / WebOTP) delivers the WHOLE code into the first box,
  // so a multi-character value is spread across the remaining boxes rather than
  // truncated to its first digit.
  function setDigit(i: number, ch: string) {
    const cleaned = ch.replace(/\D/g, '').slice(0, CODE_LENGTH - i);
    setError(null);
    setDigits((prev) => {
      const next = [...prev];
      if (cleaned.length <= 1) {
        next[i] = cleaned;
      } else {
        for (let n = 0; n < cleaned.length; n++) next[i + n] = cleaned[n];
      }
      return next;
    });
    const landed = i + Math.max(cleaned.length, 1);
    if (cleaned) focusInput(inputsRef.current[Math.min(landed, CODE_LENGTH - 1)]);
  }

  function onPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    setError(null);
    const next = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    focusInput(inputsRef.current[Math.min(pasted.length, CODE_LENGTH - 1)]);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>, i: number) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) focusInput(inputsRef.current[i - 1]);
    if (e.key === 'ArrowLeft' && i > 0) focusInput(inputsRef.current[i - 1]);
    if (e.key === 'ArrowRight' && i < CODE_LENGTH - 1) focusInput(inputsRef.current[i + 1]);
  }

  // `buttonName` defaults to the single 'resend' control the Orion screen has; the
  // Symphony screen offers SMS and WhatsApp as two separate buttons and names them
  // separately, or the two are indistinguishable in the funnel.
  async function sendCode(nextMethod: CodeMethod, buttonName = 'resend') {
    // Read the deadline rather than the ticked `resendIn`, which can be up to a tick stale — and the one
    // press worth refusing is the one made in exactly that window. The BI click is reported after this
    // guard, so a press that sent nothing never lands in the funnel as a resend.
    if (resend === 'sending' || secondsUntilResend(cooldownUntil, Date.now()) > 0) return;
    logPageClickBi({ ...PAGE, button_name: buttonName });
    setResend('sending');
    setError(null);
    // The step this press was made on — see `stepGeneration`. Once it has moved, the answer is about a
    // number the user has left, and every write below would land on the step they moved to. The outcome
    // is still REPORTED: the request was made and that is how it ended, and the refusal rate is counted
    // off that event — so each branch asks after its BI line rather than once above them all.
    const step = stepGeneration.current;
    const stale = () => step !== stepGeneration.current;
    const isWhatsApp = nextMethod === 'whatsapp';
    const deliveryOption = nextMethod === 'voice' ? 'VOICE_CALL' : isWhatsApp ? 'WHATSAPP' : 'SMS';
    try {
      const { result: data, refused, retryAfterSeconds } = await sendVerificationCode(phone, deliveryOption);
      // A 429 is one of two walls, and they want opposite copy. The TIMED one clears by itself, so it
      // becomes a wait with a countdown and the buttons come back on their own. The edge's shared budget
      // does not clear today, and telling that user to wait half a minute only buys them a second refusal —
      // so they get the dead end, which is what this branch used to say to everyone.
      //
      // `Retry-After` settles it outright when it is there. When it is not, the first bare 429 is read as
      // the timed wall (the common case, and the cheaper mistake: a needless wait rather than a wrongly
      // permanent-looking refusal) and only a second consecutive one as the budget.
      if (refused) {
        logSendCode('refused', deliveryOption);
        if (stale()) return;
        // The streak counts CONSECUTIVE bare refusals, so anything that is not one resets it: a 429 that
        // named its wait, a not-sent reply, a transport failure. Without that, a bare 429, then an
        // unrelated failure, then another bare 429 reads as "we waited and it still refuses" and shows
        // the permanent dead end to someone who only ever met the timer once.
        const timed = retryAfterSeconds !== undefined || bareRefusals.current === 0;
        bareRefusals.current = retryAfterSeconds === undefined ? bareRefusals.current + 1 : 0;
        if (timed) {
          setCooldownUntil(cooldownDeadline(Date.now(), retryAfterSeconds ?? BARE_REFUSAL_WAIT_SECONDS));
          setError({ message: '', kind: 'tooSoon' });
        } else {
          setError({ message: t('onboarding.phone.error.sendUnavailable'), kind: 'resend' });
        }
        setResend('idle');
        return;
      }
      // A refused send comes back as HTTP 200, so this has to be asked explicitly.
      if (!data || !wasCodeSent(data, isWhatsApp)) {
        logSendCode('not-sent', deliveryOption);
        if (stale()) return;
        // Not a bare 429 — see the streak note above.
        bareRefusals.current = 0;
        setError({
          message: isWhatsApp
            ? t('onboarding.phone.error.whatsappUnavailable')
            : t('onboarding.code.error.resendFailed'),
          kind: 'resend',
        });
        setResend('idle');
        return;
      }
      logSendCode('sent', deliveryOption);
      // A code DID go out, but to the number the user left — adopting its verificationId here would have
      // the new number's code checked against the old number's session and answered "wrong code".
      if (stale()) return;
      // Only a code that actually went out starts the wait. Every branch above returns: there is nothing
      // in flight to wait for, and locking the buttons for 30s after a send that never happened is a dead
      // end rather than a rate limit. A code through also clears the refusal streak — the wall that was
      // being guessed at is demonstrably not up any more.
      setCooldownUntil(cooldownDeadline(Date.now()));
      bareRefusals.current = 0;
      // Only now adopt the channel: latching it on a failed send left `submit()` posting the
      // digits down the WhatsApp path and discarding a still-valid SMS verificationId.
      if (data.verificationId) setVerificationId(data.verificationId);
      setMethod(nextMethod);
      setResend('sent');
    } catch (err) {
      logSendCode('failed', deliveryOption);
      console.warn('[send-code resend]', err);
      if (stale()) return;
      // Not a bare 429 — see the streak note above.
      bareRefusals.current = 0;
      setError({ message: t('onboarding.code.error.resendFailed'), kind: 'resend' });
      setResend('idle');
    }
  }

  return {
    phone,
    method,
    digits,
    setDigit,
    onPaste,
    onKeyDown,
    inputsRef,
    complete,
    submit,
    submitting,
    // The wait's message counts down, so it is built here from the live `resendIn` rather than from the
    // string that was stored when the 429 landed.
    // ...and only while there IS a wait. The effect that clears a spent `tooSoon` runs after the commit,
    // so at the tick the countdown reaches 0 this would format "you can ask again in 0s" for one render —
    // which is not a sentence, and `role="alert"` means a screen reader says it out loud. The phone step's
    // twin already gated on its own `blockedIn > 0`; this is the same rule.
    error:
      error?.kind === 'tooSoon'
        ? resendIn > 0
          ? t('onboarding.phone.error.tooSoon', { seconds: resendIn })
          : null
        : (error?.message ?? null),
    errorKind: error?.kind ?? null,
    phoneInUse,
    resend,
    // Seconds left before either Resend button is live again; 0 once the wait is over.
    resendIn,
    sendCode,
    logPageClickBi: (button_name: string) => logPageClickBi({ ...PAGE, button_name }),
    toPhoneStep: () => (onUseAnotherNumber ? onUseAnotherNumber() : void router.push(`${basePath}/onboarding/phone`)),
  };
}

export type CodeVerification = ReturnType<typeof useCodeVerification>;
